import clsx from "clsx"
import { CSSProperties, useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"

import { Divider, Hint, Icon } from "@atoms"

import { Select } from "@molecules"

import styles from "./FilterChip.module.scss"
import { FilterChipLockedOption, FilterChipProps } from "./FilterChip.types"
import { getValueLabel } from "./FilterChip.utils"
import FilterChipBox from "./FilterChipBox/FilterChipBox"

const WARNING_ICON = faIcon({ name: "warning", style: "solid" })

export default function FilterChip(props: FilterChipProps) {
    const {
        name,
        nameSlotLeft,

        operator,
        onOperatorClick,
        isOperatorPressed = false,
        isOperatorDisabled = false,
        operatorBoxRef,

        value,
        valueSlotLeft,
        onValueClick,
        isValuePressed = false,
        isValueDisabled = false,
        valueBoxRef,

        onRemove,
        isRemoveDisabled = false,

        isLocked = false,
        lockedOptions = [],

        isWarning = false,
        warningHint,

        noDebounce = false,
        className,
    } = props

    const isDesktop = useIsDesktop()
    const [isLockedOptionsOpen, setIsLockedOptionsOpen] = useState(false)

    const selectedOptions = lockedOptions.filter((option) => option.isSelected)
    const unselectedOptions = lockedOptions.filter((option) => !option.isSelected)
    const hasLockedOptionsList = isLocked && selectedOptions.length > 0
    const valueLabel = getValueLabel(value, selectedOptions)
    const hasOperatorBox = operator !== undefined
    const hasRemoveBox = !isLocked && onRemove !== undefined

    // The box at the chip's right end takes the chip's corners, so its fill and focus ring follow them.
    const valueBoxClassName = clsx({ [styles.endBox]: !hasRemoveBox })

    const renderDivider = () => <Divider orientation="vertical" contrast="high" className={styles.divider} />

    const renderLockedOption = (option: FilterChipLockedOption) => (
        <Select.ListItem
            key={option.value}
            variant={option.leading ? "avatar" : "default"}
            item={option}
            isSelected={option.isSelected}
            isDisabled={true}
        />
    )

    const renderNameBox = () => {
        const nameBox = (
            <FilterChipBox
                slotLeft={isWarning ? <Icon icon={WARNING_ICON} size={14} container="square" /> : nameSlotLeft}
                isWarning={isWarning}
            >
                {name}
            </FilterChipBox>
        )

        if (isWarning && warningHint !== undefined) {
            return (
                <Hint colorScheme="amber" defaultPlacement="bottom" {...warningHint}>
                    {nameBox}
                </Hint>
            )
        } else {
            return nameBox
        }
    }

    const renderValueBox = () => {
        if (hasLockedOptionsList) {
            return (
                <Select.Root selectionMode="multi" isOpen={isLockedOptionsOpen} onOpenChange={setIsLockedOptionsOpen}>
                    <Select.Trigger>
                        <FilterChipBox
                            ref={valueBoxRef}
                            className={valueBoxClassName}
                            slotLeft={valueSlotLeft}
                            isFullWidth={!isDesktop}
                            isWarning={isWarning}
                            // The popover trigger owns the click; the box only has to be interactive.
                            onClick={() => {}}
                            noDebounce={true}
                            isPressed={isLockedOptionsOpen}
                            isDisabled={isValueDisabled}
                        >
                            {valueLabel}
                        </FilterChipBox>
                    </Select.Trigger>
                    <Select.Viewport>
                        <Select.List title={name} emptyState={null}>
                            <Select.ListGroup>{selectedOptions.map(renderLockedOption)}</Select.ListGroup>
                            {unselectedOptions.length > 0 && (
                                <>
                                    <Select.ListGroupDivider />
                                    <Select.ListGroup>{unselectedOptions.map(renderLockedOption)}</Select.ListGroup>
                                </>
                            )}
                        </Select.List>
                    </Select.Viewport>
                </Select.Root>
            )
        } else {
            return (
                <FilterChipBox
                    ref={valueBoxRef}
                    className={valueBoxClassName}
                    slotLeft={valueSlotLeft}
                    isFullWidth={!isDesktop}
                    isWarning={isWarning}
                    onClick={isLocked ? undefined : onValueClick}
                    noDebounce={noDebounce}
                    isPressed={isValuePressed}
                    isDisabled={isValueDisabled}
                >
                    {valueLabel}
                </FilterChipBox>
            )
        }
    }

    return (
        <div
            role="group"
            aria-label={[name, operator, valueLabel].filter(Boolean).join(" ")}
            className={clsx(styles.base, className, {
                [styles.mobile]: !isDesktop,
                [styles.hasWarning]: isWarning,
            })}
            style={
                {
                    "--content-box-count": hasOperatorBox ? 3 : 2,
                    "--remove-box-count": hasRemoveBox ? 1 : 0,
                } as CSSProperties
            }
        >
            {renderNameBox()}
            {hasOperatorBox && (
                <>
                    {renderDivider()}
                    <FilterChipBox
                        ref={operatorBoxRef}
                        className={styles.operatorBox}
                        onClick={isLocked ? undefined : onOperatorClick}
                        noDebounce={noDebounce}
                        isPressed={isOperatorPressed}
                        isDisabled={isOperatorDisabled}
                        isWarning={isWarning}
                    >
                        {operator}
                    </FilterChipBox>
                </>
            )}
            {renderDivider()}
            {renderValueBox()}
            {hasRemoveBox && (
                <>
                    {renderDivider()}
                    <FilterChipBox
                        variant="remove"
                        className={styles.endBox}
                        onClick={onRemove}
                        noDebounce={noDebounce}
                        isDisabled={isRemoveDisabled}
                    />
                </>
            )}
        </div>
    )
}
